import {useTranslation} from 'react-i18next'
import type {
  CreativeAssetState,
  CreativeTextAlign,
  CreativeTextVertical,
  CreativeTextWeight,
} from '../../../common/utils/creativeAssetRenderer'
import {ColorRow, Slider} from './layerControls'

interface TextPanelProps {
  state: CreativeAssetState
  onChange: (patch: Partial<CreativeAssetState>) => void
}

const HEADLINE_MAX_LENGTH = 60
const SUBLINE_MAX_LENGTH = 90
const ALIGNS: CreativeTextAlign[] = ['auto', 'left', 'center', 'right']
const VERTICALS: CreativeTextVertical[] = ['auto', 'top', 'middle', 'bottom']
const WEIGHTS: CreativeTextWeight[] = ['normal', 'bolder', 'bold']
const MIN_TEXT_SCALE = 0.5
const MAX_TEXT_SCALE = 1.6

interface ChoiceRowProps<T extends string> {
  label: string
  options: readonly T[]
  value: T
  optionLabel: (option: T) => string
  onChange: (option: T) => void
}

const ChoiceRow = <T extends string>({label, options, value, optionLabel, onChange}: ChoiceRowProps<T>) => (
  <div className="caa-choice">
    <span className="caa-label">{label}</span>
    <div className="caa-tabs caa-tabs-compact">
      {options.map((option) => (
        <button
          key={option}
          type="button"
          className={`caa-tab${value === option ? ' is-active' : ''}`}
          onClick={() => onChange(option)}
        >
          {optionLabel(option)}
        </button>
      ))}
    </div>
  </div>
)

/** Copy + how it is laid out: alignment, vertical position, sizes, weight, color. */
const TextPanel = ({state, onChange}: TextPanelProps) => {
  const {t} = useTranslation()
  const formatScale = (v: number): string => `${Math.round(v * 100)}%`

  return (
    <div className="caa-layers">
      <h2 className="caa-panel-title">{t('creativeAssets.panels.text')}</h2>
      <input
        type="text"
        className="caa-text-input"
        value={state.headline}
        maxLength={HEADLINE_MAX_LENGTH}
        placeholder={t('creativeAssets.text.headlinePlaceholder')}
        aria-label={t('creativeAssets.text.headline')}
        onChange={(e) => onChange({headline: e.target.value})}
      />
      <input
        type="text"
        className="caa-text-input"
        value={state.subline}
        maxLength={SUBLINE_MAX_LENGTH}
        placeholder={t('creativeAssets.text.sublinePlaceholder')}
        aria-label={t('creativeAssets.text.subline')}
        onChange={(e) => onChange({subline: e.target.value})}
      />
      <ColorRow
        label={t('creativeAssets.text.color')}
        value={state.textColor}
        onChange={(textColor) => onChange({textColor})}
      />
      <ChoiceRow
        label={t('creativeAssets.text.align')}
        options={ALIGNS}
        value={state.textAlign}
        optionLabel={(o) => t(`creativeAssets.text.alignOptions.${o}`)}
        onChange={(textAlign) => onChange({textAlign})}
      />
      <ChoiceRow
        label={t('creativeAssets.text.vertical')}
        options={VERTICALS}
        value={state.textVertical}
        optionLabel={(o) => t(`creativeAssets.text.verticalOptions.${o}`)}
        onChange={(textVertical) => onChange({textVertical})}
      />
      <ChoiceRow
        label={t('creativeAssets.text.weight')}
        options={WEIGHTS}
        value={state.headlineWeight}
        optionLabel={(o) => t(`creativeAssets.text.weightOptions.${o}`)}
        onChange={(headlineWeight) => onChange({headlineWeight})}
      />
      <Slider
        label={t('creativeAssets.text.headlineSize')}
        value={state.headlineScale}
        min={MIN_TEXT_SCALE}
        max={MAX_TEXT_SCALE}
        step={0.05}
        format={formatScale}
        onChange={(headlineScale) => onChange({headlineScale})}
      />
      <Slider
        label={t('creativeAssets.text.sublineSize')}
        value={state.sublineScale}
        min={MIN_TEXT_SCALE}
        max={MAX_TEXT_SCALE}
        step={0.05}
        format={formatScale}
        onChange={(sublineScale) => onChange({sublineScale})}
      />
    </div>
  )
}

export default TextPanel
